<template>
  <div class="comunicado-previsualizacion-email">
    <div class="comunicado-previsualizacion-email__contenedor">
      <div class="comunicado-previsualizacion-email__tarjeta">
        <table class="comunicado-previsualizacion-email__tabla" cellpadding="0" cellspacing="0" width="100%">
          <tr>
            <td class="comunicado-previsualizacion-email__cabecera">
              <div class="comunicado-previsualizacion-email__asunto">{{ model.Asunto }}</div>
            </td>
          </tr>
          <tr>
            <td class="comunicado-previsualizacion-email__cuerpo">
              <ComunicadoCuerpoHtml :cuerpo="model.Cuerpo" />
            </td>
          </tr>
        </table>
      </div>
      <div v-if="model.rutasAdjuntos.length" class="comunicado-previsualizacion-email__adjuntos">
        <button
          v-for="adjunto in model.rutasAdjuntos"
          :key="adjunto"
          type="button"
          class="comunicado-previsualizacion-email__adjunto-chip"
          :title="getFilename(adjunto)"
          @click="verAdjunto(adjunto)"
        >
          <i class="fa-regular fa-file-lines"></i>
          <span>{{ getFilename(adjunto) }}</span>
          <i class="fa-regular fa-download comunicado-previsualizacion-email__adjunto-descarga" @click.stop="descargarAdjunto(adjunto)"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import { useModalService } from "@services/modal-service";
import DocumentoVisor from "@components/DocumentViewer.vue";
import { getDocumentalFileName as getFilename } from "@helpers/documental-file-name-helper";
import { ComunicadoPrevisualizacionModel } from "@components/comunicados/models/ComunicadoPrevisualizacionModel";
import ComunicadoCuerpoHtml from "@components/comunicados/ComunicadoCuerpoHtml.vue";
import FileSaver from "file-saver";

defineProps({
  model: {
    type: Object as () => ComunicadoPrevisualizacionModel,
    required: true,
  },
});

const api = useApi();
const modal = useModalService();

async function urlToBlob(ruta: string) {
  const response = await fetch(ruta);
  const data = await response.blob();
  return new Blob([data], { type: "application/pdf;charset=utf-8" });
}

async function verAdjunto(ruta: string) {
  try {
    const tempUrl = await api.callStandardApi("documentosGenerateTempUrl", { showLoading: false }, ruta);
    const file = ruta.split("/").pop();
    await modal.showModal(DocumentoVisor, { ruta: tempUrl }, { title: `${file}` });
  } catch (error) {
    console.error("Error al generar URL temporal para visualizar adjunto:", error);
  }
}

async function descargarAdjunto(ruta: string) {
  try {
    const tempUrl = await api.callStandardApi("documentosGenerateTempUrl", { showLoading: false }, ruta);
    const blob = await urlToBlob(tempUrl);
    const file = ruta.split("/").pop();
    FileSaver.saveAs(blob, file);
  } catch (error) {
    console.error("Error al descargar adjunto:", error);
  }
}
</script>

<style lang="scss" scoped>
.comunicado-previsualizacion-email {
  margin: 0;
  padding: var(--spacing-lg);
  font-family: Arial, sans-serif;
  background-color: #F4F5FF;
}

.comunicado-previsualizacion-email__contenedor {
  max-width: 642px;
  margin: 0 auto;
}

.comunicado-previsualizacion-email__tarjeta {
  background-color: var(--background-surface-default);
  border-radius: var(--border-radius-xl);
  overflow: hidden;
}

.comunicado-previsualizacion-email__tabla {
  width: 100%;
  border-collapse: collapse;
}

.comunicado-previsualizacion-email__cabecera {
  background-color: var(--background-primary-default);
  color: var(--text-default-inverse);
  text-align: center;
  padding: var(--spacing-lg) var(--spacing-sm);
}

.comunicado-previsualizacion-email__asunto {
  margin: 0;
  font-size: var(--font-size-heading-h2);
  font-weight: 500;
}

.comunicado-previsualizacion-email__cuerpo {
  padding: var(--spacing-lg);
  background-color: var(--background-surface-default);
}

.comunicado-previsualizacion-email__adjuntos {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-md);
}

.comunicado-previsualizacion-email__adjunto-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  max-width: 208px;
  padding: var(--spacing-xs) var(--spacing-sm);
  border: var(--border-width-sm) solid var(--border-default-default);
  border-radius: var(--border-radius-lg);
  background-color: var(--background-surface-default);
  color: var(--text-primary-default);
  font-size: var(--font-size-body-sm);
  line-height: 1.2;
  cursor: pointer;
  text-align: left;

  &:hover {
    background-color: var(--background-normal-light);
    border-color: var(--border-primary-default);
  }

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }
}

.comunicado-previsualizacion-email__adjunto-descarga {
  flex-shrink: 0;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
}
</style>
